/*  
This component prints the json-data (which we got when we hit the api) in table format. This component is called from CsVersionPage.js file
and rendered as part of Customer solution dashboard page.

This component calls Updatedtime component ( which prints last fetched data's date and time), VersionDownload component ( which gives us a 
download button when clicked it gives us a downloaded version of that particular page in pdf format). This component has table heading and
table data. It also has sort function implemeted (sorts based on arrow, when clicked ▲ it sorts that column in ascending order. When 
clicked ▼ it sorts that column based on descending order).
*/

import React, { useState, useEffect } from "react";
import './CsVersionTable.css'; 
import Updatedtime from '../UpdatedTime/Updatedtime';
import VersionDownload from "../Download/VersionDownload";
import loader from "../../assets/loader.gif";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CsVersionTable = () => {
  const [versionData, setVersionData] = useState([]); 
  const [versionTime, setVersionTime] = useState('');  
  const [loading, setLoading] = useState(false); 
  const [error, setError] = useState(null);  
  const [sortConfig, setSortConfig] = useState({
    key: 'microsvc',
    direction: 'asc'
  });

  useEffect(() => {
    const fetchVer = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_BASE_URL}/cst/cst-info`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log('Initial fetch response:', data);  // Debug log

        setVersionData(data.data || []);
        if (data.date_time === undefined) {
          console.warn('No date_time in response');
        }

        if (data.date_time) {  
          setVersionTime(data.date_time);
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVer();
  }, []);  

  const handleSort = (key, direction) => {
    const sortedData = [...versionData].sort((a, b) => {
      const aValue = (a[key] || '').toLowerCase();
      const bValue = (b[key] || '').toLowerCase();

      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });

    setVersionData(sortedData);
    setSortConfig({ key, direction });
  };

  const refreshEnv = async () => {
    setLoading(true);
    setError(null);
    try {
      
      const response = await fetch(`${API_BASE_URL}/cst/cache/refresh`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Refresh response:', data);                     // Debug log


      setVersionData(data.data || []);
      if (data.date_time) {                             // Only update time if we actually got a value
        setVersionTime(data.date_time);
      } else {                                         //Keep the previous time if no new time is provided
        console.log('Keeping previous time due to missing date_time in refresh response');
      }
    } catch (err) {
      console.error('Refresh error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="table-container-cs">
      <div className="time-container-cs">
          <Updatedtime 
            environmentTime={versionTime} 
            loading={loading}
            error={error}
          />
          <button 
            onClick={refreshEnv} 
            className="refresh-button-cs"
            data-tooltip="Refresh"
          >
            ↻
          </button>
          <div>
            <VersionDownload 
                versionData={versionData} 
                versionTime={versionTime} 
                versionTableTitle="CS Version"
                columns={["Microservice", "LIT", "Staging", "Production"]}
                columnKeys={["microsvc", "dev", "stg", "prod"]}
                filenamePrefix="cs_version"
            />
          </div>
          
      </div>
      <div className="cs-table-div">
        <table className="cs-table">
          <thead className="cs-table-thead">
            <tr>
            <th className="cs-th">
                <div className="table-header-content-cs">
                  <span className="table-heading-cs-mvc">Microservice</span>
                  <div className="sort-arrows-cs">
                    <span 
                      onClick={() => handleSort('microsvc', 'asc')} 
                      className={`arrow-cs ${sortConfig.key === 'microsvc' && sortConfig.direction === 'asc' ? 'active' : ''}`}
                    >
                      ▲
                    </span>
                    <span 
                      onClick={() => handleSort('microsvc', 'desc')} 
                      className={`arrow-cs ${sortConfig.key === 'microsvc' && sortConfig.direction === 'desc' ? 'active' : ''}`}
                    >
                      ▼
                    </span>
                  </div>
                </div>
              </th>
              <th className="cs-th">CS-LIT</th>
              <th className="cs-th">CS-Staging</th>
              <th className="cs-th">CS-Production</th>
            </tr>
          </thead>
          <tbody className="cs-table-div-tbody">
          {loading && (
              <tr>
                <td colSpan='4' className="status-message">
                  <div className="loading">
                      <img src={loader} alt="Loading..." />
                  </div>
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan='4' className="status-message-error">Error: {error}</td>
              </tr>
            )}
            {!loading && !error  && versionData.length ===0 && (
              <tr>
                <td colSpan='4' className="status-message">No data found</td>
              </tr>
            )}
            {!loading && !error && versionData.map((item, index) => (
              <tr key={index} className="tr-cs">
                <td className="cs-td">{item['microsvc']}</td>
                <td className="cs-td">{item['dev']}</td>
                <td className="cs-td">{item['stg'] || 'N/A'}</td>
                <td className="cs-td">{item['prod']}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CsVersionTable;
